import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Chip,
  Divider,
  Stack
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  border: '#e8e4de',
};

// statusConfig moved inside component to use t()

// friendlyMessages, provisioningTips moved inside component to use t()

function ApplyStatus() {
  const { t } = useTranslation('auth');
  const { id } = useParams();
  const [loading, setLoading] = useState(true);

  const statusConfig = {
    pending: { label: t('apply_status.status_pending'), color: 'warning', icon: PendingIcon },
    approved: { label: t('apply_status.status_approved'), color: 'info', icon: CheckCircleIcon },
    provisioning: { label: t('apply_status.status_processing'), color: 'info', icon: PendingIcon },
    completed: { label: t('apply_status.status_completed'), color: 'success', icon: CheckCircleIcon },
    rejected: { label: t('apply_status.status_failed'), color: 'error', icon: ErrorIcon },
  };

  const friendlyMessages = {
    'Creating tenant': t('apply_status.step_create_tenant'),
    'Creating database': t('apply_status.step_create_db'),
    'Creating admin user': t('apply_status.step_create_admin'),
    'Setting up template': t('apply_status.step_setup_template'),
    'Configuring settings': t('apply_status.step_configure'),
    'Provisioning completed': t('apply_status.step_complete'),
    'Creating tenant settings': t('apply_status.step_create_settings'),
    'Setting up domain': t('apply_status.step_setup_domain'),
  };

  const getFriendlyMessage = (msg) => {
    if (!msg) return msg;
    for (const [key, friendly] of Object.entries(friendlyMessages)) {
      if (msg.toLowerCase().includes(key.toLowerCase())) return friendly;
    }
    return msg;
  };

  const provisioningTips = [
    { title: t('apply_status.tip_1_title'), desc: t('apply_status.tip_1_desc') },
    { title: t('apply_status.tip_2_title'), desc: t('apply_status.tip_2_desc') },
    { title: t('apply_status.tip_3_title'), desc: t('apply_status.tip_3_desc') },
  ];
  const [error, setError] = useState('');
  const [application, setApplication] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = async () => {
    try {
      const response = await api.get(`/apply/${id}/status`);
      if (response.data.success) {
        setApplication(response.data.data);
        // 如果已完成或失敗，停止自動更新
        if (['completed', 'rejected'].includes(response.data.data.status)) {
          setAutoRefresh(false);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('apply_status.loading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [id]);

  // 自動更新狀態
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRefresh, id]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: palette.accent }} />
          <Typography sx={{ mt: 2, color: palette.textMuted }}>{t('apply_status.loading')}</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button component={Link} to="/apply" sx={{ mt: 2, color: palette.primary }}>
            {t('apply_status.reapply')}
          </Button>
        </Container>
      </Box>
    );
  }

  const status = statusConfig[application.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  const tenantUrl = application.tenantUrl || '';
  const adminUrl = application.adminUrl || '';
  const adminBase = adminUrl ? adminUrl.replace(/\/$/, '') : '';
  const tenantBase = tenantUrl ? tenantUrl.replace(/\/$/, '') : '';

  const nextSteps = [
    {
      label: t('apply_status.next_step_1'),
      desc: t('apply_status.next_step_1_desc'),
      href: adminBase ? `${adminBase}/products` : undefined,
      btnText: t('apply_status.next_step_1_btn'),
      testId: 'apply-status-next-products',
      variant: 'contained',
    },
    {
      label: t('apply_status.next_step_2'),
      desc: t('apply_status.next_step_2_desc'),
      href: adminBase ? `${adminBase}/tenant-settings` : undefined,
      btnText: t('apply_status.next_step_2_btn'),
      testId: 'apply-status-next-settings',
      variant: 'outlined',
    },
    {
      label: t('apply_status.next_step_3'),
      desc: t('apply_status.next_step_3_desc'),
      href: tenantBase ? `${tenantBase}/products` : undefined,
      btnText: t('apply_status.next_step_3_btn'),
      testId: 'apply-status-next-test-order',
      variant: 'outlined',
    },
  ];

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh', position: 'relative' }}>
      {/* Top gradient bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent}, ${palette.secondary})`,
          zIndex: 10,
        }}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          }}
        >
          {/* 標題區 */}
          <Box sx={{ textAlign: 'center', mb: 4, position: 'relative' }}>
            {/* Decorative dots for completed state */}
            {application.status === 'completed' && (
              <>
                <Box sx={{ position: 'absolute', top: 8, left: '15%', width: 6, height: 6, borderRadius: '50%', bgcolor: `${palette.accent}40` }} />
                <Box sx={{ position: 'absolute', top: 24, right: '12%', width: 8, height: 8, borderRadius: '50%', bgcolor: `${palette.secondary}30` }} />
                <Box sx={{ position: 'absolute', bottom: 10, left: '20%', width: 5, height: 5, borderRadius: '50%', bgcolor: `${palette.primary}25` }} />
              </>
            )}
            <StatusIcon sx={{ fontSize: 64, color: `${status.color}.main`, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 950, color: palette.textDark }} gutterBottom>
              {application.platformName}
            </Typography>
            <Chip
              label={status.label}
              color={status.color}
              sx={{ fontSize: '1rem', py: 2, px: 1, fontWeight: 700 }}
            />
          </Box>

          {/* 申請資訊 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ color: palette.textMuted }}>{t('apply_status.application_id')}</Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace', color: palette.textDark }}>
              {application.applicationId}
            </Typography>

            <Typography variant="subtitle2" sx={{ color: palette.textMuted }}>{t('apply_status.subdomain')}</Typography>
            <Typography variant="body1" sx={{ mb: 2, color: palette.textDark }}>
              {application.subdomain}.urstore.tw
            </Typography>

            <Typography variant="subtitle2" sx={{ color: palette.textMuted }}>{t('apply_status.plan')}</Typography>
            <Typography variant="body1" sx={{ mb: 2, color: palette.textDark }}>
              {application.planName || t('apply_status.plan')}
            </Typography>
          </Box>

          <Divider sx={{ my: 3, borderColor: palette.border }} />

          {/* 佈建完成 - 顯示連結與登入資訊 */}
          {application.status === 'completed' && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 3,
                '& .MuiAlert-icon': { alignItems: 'center' },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 950 }} gutterBottom>
                {t('apply_status.completed_title')}
              </Typography>

              {/* 登入資訊區塊 */}
              {application.credentials && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    my: 2,
                    bgcolor: `${palette.primary}06`,
                    borderColor: `${palette.primary}20`,
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.5, color: palette.textDark }}>
                    {t('apply_status.credentials_title')}
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ color: palette.textMuted, minWidth: 70 }}>
                        {t('apply_status.credentials_account')}
                      </Typography>
                      <Box
                        component="code"
                        sx={{
                          bgcolor: `${palette.primary}08`,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 1.5,
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: palette.textDark,
                          border: `1px solid ${palette.primary}15`,
                        }}
                      >
                        {application.credentials.username}
                      </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ color: palette.textMuted, minWidth: 70 }}>
                        {t('apply_status.credentials_password')}
                      </Typography>
                      <Box
                        component="code"
                        sx={{
                          bgcolor: `${palette.accent}18`,
                          color: palette.textDark,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 1.5,
                          fontWeight: 900,
                          fontFamily: 'monospace',
                          border: `1px solid ${palette.accent}30`,
                        }}
                      >
                        {showPassword ? application.credentials.tempPassword : '••••••••'}
                      </Box>
                      <Tooltip title={showPassword ? t('apply_status.hide_password') : t('apply_status.show_password')}>
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          sx={{ color: palette.textMuted }}
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copied ? t('apply_status.copied') : t('apply_status.copy_password')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(application.credentials.tempPassword);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          sx={{ color: copied ? palette.secondary : palette.textMuted }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5, fontWeight: 700 }}>
                    {t('apply_status.credentials_warning')}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  href={application.adminUrl}
                  target="_blank"
                  startIcon={<LaunchIcon />}
                  sx={{
                    mr: 2,
                    mb: 1,
                    bgcolor: palette.primary,
                    fontWeight: 900,
                    borderRadius: 2.5,
                    '&:hover': { bgcolor: '#152b46' },
                  }}
                >
                  {t('apply_status.go_to_admin')}
                </Button>
                <Button
                  variant="outlined"
                  href={application.tenantUrl}
                  target="_blank"
                  startIcon={<LaunchIcon />}
                  sx={{
                    mb: 1,
                    borderColor: palette.border,
                    color: palette.primary,
                    fontWeight: 900,
                    borderRadius: 2.5,
                    '&:hover': { borderColor: palette.primary },
                  }}
                >
                  {t('apply_status.go_to_store')}
                </Button>
              </Box>

              {!application.credentials && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {t('apply_status.password_sent_email')}
                </Typography>
              )}
            </Alert>
          )}

          {/* Activation: next steps */}
          {application.status === 'completed' && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                borderColor: palette.border,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 950, mb: 1, color: palette.textDark }}>
                {t('apply_status.next_steps_title')}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2.5, lineHeight: 1.7 }}>
                {t('apply_status.next_steps_subtitle')}
              </Typography>

              <Stack spacing={1.5}>
                {nextSteps.map((step, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      borderColor: palette.border,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                        borderColor: palette.primary,
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      {/* Numbered circle */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: palette.primary,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 900,
                          fontSize: '0.85rem',
                          flexShrink: 0,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                          {step.label}
                        </Typography>
                        {step.desc && (
                          <Typography variant="caption" sx={{ color: palette.textMuted, lineHeight: 1.5 }}>
                            {step.desc}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant={step.variant}
                        href={step.href}
                        target="_blank"
                        disabled={!step.href}
                        data-testid={step.testId}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          flexShrink: 0,
                          ...(step.variant === 'contained'
                            ? {
                                bgcolor: palette.primary,
                                '&:hover': { bgcolor: '#152b46' },
                              }
                            : {
                                borderColor: palette.border,
                                color: palette.primary,
                                '&:hover': { borderColor: palette.primary },
                              }),
                        }}
                      >
                        {step.btnText}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              <Divider sx={{ my: 2, borderColor: palette.border }} />
              <Typography variant="body2" sx={{ color: palette.textMuted }}>
                {t('apply_status.no_email_hint', { link: '' })} <Link to="/contact" style={{ color: palette.primary }}>{t('apply_status.contact_support')}</Link>
              </Typography>
            </Paper>
          )}

          {/* 佈建失敗 */}
          {application.status === 'rejected' && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 950 }} gutterBottom>
                {t('apply_status.rejected_title')}
              </Typography>
              <Typography variant="body2">
                {application.rejectReason || t('apply_status.rejected_reason')}
              </Typography>
              <Button
                component={Link}
                to="/apply"
                variant="outlined"
                sx={{
                  mt: 2,
                  borderColor: palette.border,
                  color: palette.primary,
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                {t('apply_status.reapply')}
              </Button>
            </Alert>
          )}

          {/* 處理中 - 友善化進度顯示 */}
          {['pending', 'approved', 'provisioning'].includes(application.status) && (
            <Box sx={{ mb: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  borderColor: `${palette.primary}25`,
                  bgcolor: `${palette.primary}04`,
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress size={24} sx={{ color: palette.accent }} />
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                        {application.status === 'pending' && t('apply_status.pending_message')}
                        {application.status === 'approved' && t('apply_status.approved_message')}
                        {application.status === 'provisioning' && t('apply_status.provisioning_message')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textMuted }}>
                        {t('apply_status.estimated_time')}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Visual progress bar */}
                  <Box>
                    <LinearProgress
                      variant={application.status === 'provisioning' ? 'indeterminate' : 'buffer'}
                      value={application.status === 'pending' ? 10 : 40}
                      valueBuffer={application.status === 'pending' ? 30 : 60}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: `${palette.accent}15`,
                        '& .MuiLinearProgress-bar': { bgcolor: palette.accent, borderRadius: 3 },
                        '& .MuiLinearProgress-bar2Buffer': { bgcolor: `${palette.accent}30` },
                        '& .MuiLinearProgress-dashed': { display: 'none' },
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>

              {/* Helpful tips during wait */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: palette.textDark, mb: 1.5 }}>
                  {t('apply_status.tips_title')}
                </Typography>
                <Stack spacing={1.5}>
                  {provisioningTips.map((tip, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        borderColor: palette.border,
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: palette.accent, bgcolor: `${palette.accent}04` },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: palette.textDark, mb: 0.5 }}>
                        {tip.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textMuted, lineHeight: 1.7 }}>
                        {tip.desc}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Box>
          )}

          {/* 佈建日誌 - 友善化顯示 */}
          {application.logs && application.logs.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 950, color: palette.textDark }}>{t('apply_status.progress_title')}</Typography>
                {autoRefresh && (
                  <Chip
                    icon={<RefreshIcon />}
                    label={t('apply_status.auto_refresh')}
                    size="small"
                    sx={{
                      bgcolor: `${palette.primary}10`,
                      color: palette.primary,
                      border: `1px solid ${palette.primary}25`,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>

              <Stepper
                orientation="vertical"
                sx={{
                  '& .MuiStepIcon-root.Mui-active': { color: palette.accent },
                  '& .MuiStepIcon-root.Mui-completed': { color: palette.secondary },
                }}
              >
                {application.logs.slice().reverse().map((log, index) => (
                  <Step key={index} active completed={log.status === 'success'}>
                    <StepLabel
                      error={log.status === 'failed'}
                      optional={
                        <Typography variant="caption" sx={{ color: palette.textMuted }}>
                          {new Date(log.created_at).toLocaleString('zh-TW')}
                        </Typography>
                      }
                    >
                      {getFriendlyMessage(log.message)}
                    </StepLabel>
                    <StepContent>
                      {log.status === 'running' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={16} />
                          <Typography variant="caption" sx={{ color: palette.textMuted }}>
                            {t('apply_status.processing')}
                          </Typography>
                        </Stack>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {/* 重新整理按鈕 */}
          {!autoRefresh && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAutoRefresh(true);
                  fetchStatus();
                }}
                startIcon={<RefreshIcon />}
                sx={{
                  borderColor: palette.border,
                  color: palette.primary,
                  borderRadius: 2,
                  fontWeight: 700,
                  '&:hover': { borderColor: palette.primary },
                }}
              >
                {t('btn.refresh', { ns: 'common' })}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default ApplyStatus;
